import * as React from 'react';
import { AlertCircle, X } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ParsedTRPCError } from '@/lib/utils/trpc-error-utils';

interface ErrorDisplayProps {
  error: ParsedTRPCError | null;
  onDismiss?: () => void;
  className?: string;
}

/**
 * Formats a field path to be more user-friendly
 * e.g., "data.prices" -> "Prices", "user.email" -> "Email"
 */
function formatFieldName(field: string): string {
  // Split by dots and take the last part (most specific field)
  const parts = field.split('.');
  const lastPart = parts[parts.length - 1];

  // Convert camelCase/snake_case to Title Case
  return lastPart
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/_/g, ' ') // Replace underscores with spaces
    .trim()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Component to display TRPC errors with field-specific validation messages
 */
export function ErrorDisplay({ error, onDismiss, className }: ErrorDisplayProps) {
  if (!error) return null;

  const fieldErrorCount = error.fieldErrors ? Object.keys(error.fieldErrors).length : 0;

  return (
    <div
      className={`relative rounded-lg border border-orange-200 bg-orange-50/50 px-4 py-3.5 ${className || ''}`}
    >
      <div className="flex items-start gap-3">
        <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-orange-600" />

        <div className="min-w-0 flex-1 pr-6">
          <p className="text-sm font-medium text-orange-900">{error.message}</p>

          {error.isValidationError && error.fieldErrors && fieldErrorCount > 0 && (
            <div className="mt-2.5 space-y-1.5">
              {Object.entries(error.fieldErrors).map(([field, messages]) => (
                <div key={field} className="flex items-center gap-2 text-sm">
                  <span className="h-1 w-1 flex-shrink-0 rounded-full bg-orange-400" />
                  <span className="text-orange-800">
                    <span className="font-medium text-orange-900">{formatFieldName(field)}</span>
                    <span className="text-orange-700"> · {messages.join(', ')}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {onDismiss && (
          <button
            type="button"
            className="absolute right-2 top-2 rounded p-1 text-orange-400 transition-colors hover:bg-orange-100 hover:text-orange-600"
            onClick={onDismiss}
          >
            <X className="h-3.5 w-3.5" />
            <span className="sr-only">Dismiss</span>
          </button>
        )}
      </div>
    </div>
  );
}

interface FieldErrorProps {
  fieldErrors: Record<string, string[]>;
  fieldName: string;
  className?: string;
}

/**
 * Component to display field-specific error messages inline
 */
export function FieldError({ fieldErrors, fieldName, className }: FieldErrorProps) {
  const errors = fieldErrors[fieldName];

  if (!errors || errors.length === 0) return null;

  return <p className={`mt-1 text-xs leading-tight text-red-600 ${className || ''}`}>{errors.join(' ')}</p>;
}
